import React, {useState, useEffect} from 'react';
import {Box, Button, Modal, Select, SpaceBetween, Spinner, StatusIndicator} from '@cloudscape-design/components';
import type {Credentials} from 'aws-sdk';
import {Streamdown} from 'streamdown';
import {ChartRenderer} from './chart-renderer';
import {getArtifactContent, getArtifactDownloadUrl, listArtifacts} from './cassandra-api';
import type {Artifact} from './cassandra-api';
import {CHART_MIME_TYPE, VALID_CHART_TYPES} from './cassandra-types';

const PANEL_STREAMDOWN_CONTROLS = {table: false, code: false, mermaid: false} as const;

export function ArtifactContent({content, mimeType, imageUrl}: {content: string | null; mimeType: string; imageUrl?: string}) {
    if (mimeType.startsWith('image/') && imageUrl) {
        return <img src={imageUrl} alt="Artifact" style={{maxWidth: '100%', borderRadius: 8}} />;
    }

    if (!content) return <StatusIndicator type="error">No content</StatusIndicator>;

    if (mimeType === CHART_MIME_TYPE) {
        try {
            const parsed = JSON.parse(content);
            if (parsed.type && (VALID_CHART_TYPES as readonly string[]).includes(parsed.type)) {
                return <ChartRenderer type={parsed.type} props={parsed.props || {}} />;
            }
        } catch {
            /* fall through */
        }
    }

    if (mimeType === 'application/json') {
        try {
            const formatted = JSON.stringify(JSON.parse(content), null, 2);
            return (
                <div className="cassandra-streamdown-scope">
                    <Streamdown controls={PANEL_STREAMDOWN_CONTROLS}>{`\`\`\`json\n${formatted}\n\`\`\``}</Streamdown>
                </div>
            );
        } catch {
            /* fall through */
        }
    }

    if (mimeType === 'text/csv') {
        const lines = content.trim().split('\n');
        if (lines.length > 0) {
            const rows = lines.map((line) => line.split(',').map((cell) => cell.trim()));
            const header = `| ${rows[0].join(' | ')} |`;
            const separator = `| ${rows[0].map(() => '---').join(' | ')} |`;
            const body = rows
                .slice(1)
                .map((row) => `| ${row.join(' | ')} |`)
                .join('\n');
            return (
                <div className="cassandra-streamdown-scope">
                    <Streamdown controls={PANEL_STREAMDOWN_CONTROLS}>{`${header}\n${separator}\n${body}`}</Streamdown>
                </div>
            );
        }
    }

    return (
        <div className="cassandra-streamdown-scope">
            <Streamdown controls={PANEL_STREAMDOWN_CONTROLS}>{content}</Streamdown>
        </div>
    );
}

export function ArtifactPanelContent({
    artifactId: initialArtifactId,
    mimeType: initialMimeType,
    name: initialName,
    chatId,
    credentials,
}: {
    artifactId: string;
    mimeType: string;
    name: string;
    chatId: string;
    credentials: Credentials;
}) {
    const [artifacts, setArtifacts] = useState<Artifact[]>([]);
    const [selectedId, setSelectedId] = useState(initialArtifactId);
    const [content, setContent] = useState<string | null>(null);
    const [imageUrl, setImageUrl] = useState<string | undefined>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(false);

    const selectedArtifact = artifacts.find((a) => a.artifactId === selectedId);
    const currentMimeType = selectedArtifact?.mimeType ?? initialMimeType;
    const currentName = selectedArtifact?.name ?? initialName;
    const isImage = currentMimeType.startsWith('image/');

    useEffect(() => {
        listArtifacts(chatId, credentials)
            .then((list) => {
                setArtifacts(list);
                if (list.length > 0 && !list.find((a) => a.artifactId === initialArtifactId)) {
                    setSelectedId(list[0].artifactId);
                }
            })
            .catch(() => {});
    }, [chatId, credentials, initialArtifactId]);

    useEffect(() => {
        setSelectedId(initialArtifactId);
    }, [initialArtifactId]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setContent(null);
        setImageUrl(undefined);

        if (isImage) {
            getArtifactDownloadUrl(chatId, selectedId, credentials, currentName)
                .then((url) => {
                    if (!cancelled) setImageUrl(url);
                })
                .catch((e) => {
                    if (!cancelled) setError(e.message ?? 'Failed to load image');
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });
        } else {
            getArtifactContent(chatId, selectedId, credentials)
                .then((result) => {
                    if (!cancelled) {
                        const c = typeof result === 'string' ? result : result.content ?? '';
                        setContent(c as string);
                    }
                })
                .catch((e) => {
                    if (!cancelled) setError(e.message ?? 'Failed to load artifact');
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });
        }

        return () => {
            cancelled = true;
        };
    }, [selectedId, chatId, credentials, isImage, currentName]);

    const handleDownload = async () => {
        try {
            const url = await getArtifactDownloadUrl(chatId, selectedId, credentials, currentName);
            const link = document.createElement('a');
            link.href = url;
            link.download = currentName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            console.error('Download failed:', e);
        }
    };

    const dropdownOptions = artifacts.map((a) => ({label: a.name, value: a.artifactId}));

    const renderedContent = loading ? (
        <Box textAlign="center" padding="xl">
            <Spinner size="large" />
        </Box>
    ) : error ? (
        <StatusIndicator type="error">{error}</StatusIndicator>
    ) : (
        <ArtifactContent content={content} mimeType={currentMimeType} imageUrl={imageUrl} />
    );

    return (
        <>
            <SpaceBetween size="s">
                <div className="cassandra-artifact-panel-header">
                    {dropdownOptions.length > 1 && (
                        <div style={{flex: 1, minWidth: 0}}>
                            <Select
                                selectedOption={dropdownOptions.find((o) => o.value === selectedId) ?? null}
                                onChange={({detail}) => {
                                    if (detail.selectedOption.value) setSelectedId(detail.selectedOption.value);
                                }}
                                options={dropdownOptions}
                                placeholder="Select artifact"
                            />
                        </div>
                    )}
                    <SpaceBetween direction="horizontal" size="xs">
                        <div className="cassandra-tooltip" data-tooltip="Download">
                            <Button iconName="download" variant="icon" ariaLabel="Download" onClick={handleDownload} />
                        </div>
                        <div className="cassandra-tooltip" data-tooltip="Expand">
                            <Button iconName="expand" variant="icon" ariaLabel="Expand" onClick={() => setExpanded(true)} />
                        </div>
                    </SpaceBetween>
                </div>
                {renderedContent}
            </SpaceBetween>

            {expanded && (
                <Modal
                    visible
                    onDismiss={() => setExpanded(false)}
                    header={currentName}
                    size="max"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button iconName="download" onClick={handleDownload}>
                                    Download
                                </Button>
                                <Button variant="primary" onClick={() => setExpanded(false)}>
                                    Close
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <div style={{minHeight: 400}}>{renderedContent}</div>
                </Modal>
            )}
        </>
    );
}
